import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  effect,
  OnInit,
  Output,
  ViewChild,
  inject,
  Input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { type MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { SvgIconComponent } from '../../shared/svg-icon/svg-icon.component';
import { FileItem, LinkPatch } from '../../models';
import { FileManagerService } from '../../services/file-manager.service';
import type { LinkedFileItem, DocMatrixLink, DocMatrixNavigationTarget } from '../../models/api';
import { FileManagerQuota } from './sub-components/file-manager-quota.component';
import { FileManagerPlugins } from './sub-components/file-manager-plugins.component';
import { FileUploadStatus } from './sub-components/file-upload-status.component';
import { SearchBarComponent, SearchQuery } from './sub-components/search-bar.component';
import { FileActionBar } from './sub-components/file-action-bar.component';
import { FileGridView } from './sub-components/file-grid-view.component';
import { FileListView } from './sub-components/file-list-view.component';
import { NewFolderDialog } from './sub-components/dialogs/new-folder-dialog.component';
import { DeleteConfirmDialog } from './sub-components/dialogs/delete-confirm-dialog.component';
import { LinkedItemErrorDialog } from './sub-components/dialogs/linked-item-error-dialog.component';
import { FileLinksDialog } from './sub-components/dialogs/file-links-dialog.component';
import { FilePreviewDialog } from './sub-components/dialogs/file-preview-dialog.component';
import { ScanPollingService } from '../../services/scan-polling.service';
import { FileDropDirective } from '../../directives/file-drop.directive';
import { UploadHistoryStore } from '../../services/upload-history.store';
import { UploadQueueStore } from '../../services/upload-queue.store';
import { FileMoveStore } from '../../services/file-move.store';
import { FileBrowserStore } from '../../services/file-browser.store';
import { UiStateService } from '../../services/ui-state.service';
import { ATTACHMENTS_HANDLER_CONFIG } from '../../config/attachments-handler.config';
import { isImageExtension } from './file-type.utils';
import { buildDragImage } from './drag-image.utils';
import { FilesystemFilterService } from '../../services';

@Component({
  selector: 'app-right-panel',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    SelectButton,
    ProgressBarModule,
    BreadcrumbModule,
    SvgIconComponent,
    FileManagerQuota,
    FileManagerPlugins,
    FileUploadStatus,
    SearchBarComponent,
    FileGridView,
    FileActionBar,
    FileListView,
    NewFolderDialog,
    DeleteConfirmDialog,
    LinkedItemErrorDialog,
    FileLinksDialog,
    FilePreviewDialog,
    Menu,
  ],
  templateUrl: './right-panel.html',
  styleUrl: './right-panel.scss',
  providers: [FileBrowserStore, UploadQueueStore, FileMoveStore],
  hostDirectives: [
    {
      directive: FileDropDirective,
      outputs: ['dragStateChange'],
    },
  ],
})
export class RightPanel implements OnInit {
  @Input() currentPath = 'tao://';
  @Input() hasSelectedCategory = false;
  @Input() panelId = 'right';
  @Input() showFileBrowserButton = false;
  @Input() fileBrowserActive = false;
  @Input() moveOnly = false;
  @Input() currentSourcemodelId: number | null = null;

  private paneVisible = true;
  private pendingRefresh = false;

  @Input() set visible(value: boolean) {
    this.paneVisible = value;
    if (value && this.pendingRefresh) {
      this.pendingRefresh = false;
      this.loadFiles();
    }
  }

  fileLinkStatusMap = new Map<string, string[]>();

  @Input() set allLinks(links: LinkedFileItem[]) {
    this.fileLinkStatusMap = new Map();
    for (const link of links) {
      const status = link.context?.voucher_status;
      if (status) {
        const name = link.file.name;
        const existing = this.fileLinkStatusMap.get(name) ?? [];
        existing.push(status);
        this.fileLinkStatusMap.set(name, existing);
      }
    }
  }

  @Output() linkSelectedFiles = new EventEmitter<FileItem[]>();
  @Output() scanComplete = new EventEmitter<void>();
  @Output() navigateToDocMatrixItem = new EventEmitter<DocMatrixNavigationTarget>();
  @Output() filesMoved = new EventEmitter<string>();
  @Output() fileBrowserToggle = new EventEmitter<void>();
  @Output() previewVisibleChange = new EventEmitter<boolean>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('folderInput') folderInput!: ElementRef<HTMLInputElement>;
  @ViewChild(FileListView) private fileListView?: FileListView;
  @ViewChild('newMenu') newMenu!: Menu;

  readonly newMenuItems: MenuItem[] = [
    {
      label: 'Új mappa',
      icon: 'pi pi-folder',
      command: () => this.openNewFolderDialog(),
    },
    {
      label: 'Fájl feltöltés',
      icon: 'pi pi-upload',
      command: () => this.triggerFileUpload(),
    },
    {
      label: 'Mappa feltöltés',
      icon: 'pi pi-folder-open',
      command: () => this.triggerFolderUpload(),
    },
  ];

  private readonly api = inject(FileManagerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly http = inject(HttpClient);
  private readonly scanPolling = inject(ScanPollingService);
  protected readonly uploadHistory = inject(UploadHistoryStore);
  protected readonly uploadQueue = inject(UploadQueueStore);
  private readonly fileMove = inject(FileMoveStore);
  protected readonly browserStore = inject(FileBrowserStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fileDrop = inject(FileDropDirective);
  protected readonly uiState = inject(UiStateService);
  protected readonly filesystemFilter = inject(FilesystemFilterService);
  private readonly config = inject(ATTACHMENTS_HANDLER_CONFIG);
  private readonly sanitizer = inject(DomSanitizer);

  // ── File list state (delegated to browserStore) ──
  selectedFiles: FileItem[] = [];
  readonly highlightedFileName = signal<string | null>(null);

  get files(): FileItem[] { return this.browserStore.files(); }
  get loading(): boolean { return this.browserStore.loading(); }
  get storages(): string[] { return this.browserStore.storages(); }
  get readOnly(): boolean { return this.browserStore.readOnly(); }


  breadcrumbItems: MenuItem[] = [];
  homeItem: MenuItem = { icon: 'pi pi-home', command: () => this.navigateToBreadcrumb(0) };

  constructor() {
    effect(() => {
      const segments = this.browserStore.breadcrumbs();
      const pathParts = segments.slice(1);
      this.homeItem = {
        icon: 'pi pi-home',
        command: () => this.navigateToBreadcrumb(0),
        styleClass: pathParts.length === 0 ? 'p-breadcrumb-item--active' : '',
      };
      this.breadcrumbItems = pathParts.map((segment, index) => ({
        label: segment,
        command: () => this.navigateToBreadcrumb(index + 1),
        styleClass: index === pathParts.length - 1 ? 'p-breadcrumb-item--active' : '',
      }));
    });
  }

  viewMode: 'list' | 'grid' = 'list';
  viewOptions = [
    { icon: 'list', value: 'list' },
    ...(this.config.gridSystemEnabled !== false ? [{ icon: 'grid', value: 'grid' }] : []),
  ];

  // ── Drag ──
  isDragOver = false;

  // ── Dialogs ──
  showNewFolderDialog = false;

  showDeleteDialog = false;
  deleteTarget: FileItem | null = null;
  deleteMessage = '';

  showLinkedItemErrorDialog = false;
  linkedItemErrorMessage = '';

  showLinksDialog = false;
  linksDialogFileName = '';
  linksDialogLinks: DocMatrixLink[] = [];

  showPreviewDialog = false;
  previewUrl = '';
  previewFileName = '';
  previewMimeType = '';

  // ── Inline (side-pane) preview ──
  inlinePreviewType: 'pdf' | 'image' | null = null;
  inlinePreviewName = '';
  inlinePreviewRawUrl = '';
  inlinePreviewSafeUrl: SafeResourceUrl | null = null;

  private pollSubscription?: Subscription;

  private stopScanPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private startScanPollingIfNeeded(): void {
    this.stopScanPolling();
    if (!this.files.some((f) => f.isScanning)) return;
    if (!this.paneVisible) {
      this.pendingRefresh = true;
      return;
    }

    const polledPath = this.currentPath;
    this.pollSubscription = this.scanPolling
      .pollUntilScanComplete(polledPath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entries) => {
          if (this.currentPath !== polledPath) {
            this.stopScanPolling();
            return;
          }
          this.browserStore.setFilesFromEntries(entries);
          this.cdr.markForCheck();
        },
        complete: () => {
          this.uploadHistory.markScanned();
          this.scanComplete.emit();
          setTimeout(() => {
            this.quotaRefreshTrigger++;
            this.cdr.markForCheck();
          });
        },
      });
  }

  // ── Upload ──
  quotaRefreshTrigger = 0;

  /** The single upload list and panel state are owned by the store. */
  readonly allUploads = this.uploadHistory.uploads;

  get folders() { return this.files.filter((f) => f.type === 'folder'); }
  get filesList() { return this.files.filter((f) => f.type === 'file'); }
  get currentStorage(): string { return this.currentPath.split('://')[0] ?? ''; }
  get isGridEnabled(): boolean { return this.config.gridSystemEnabled ?? true; }

  get showUploadDialog(): boolean { return this.uploadHistory.visible(); }
  get isUploadPanelExpanded(): boolean { return this.uploadHistory.expanded(); }
  get isUploading(): boolean { return this.uploadHistory.isUploading(); }
  get uploadProgress(): number { return this.uploadHistory.progress(); }
  get isUploadComplete(): boolean { return this.uploadHistory.isComplete(); }
  get uploadPanelTitle(): string { return this.uploadHistory.panelTitle(); }

  // ── Lifecycle ──

  ngOnInit(): void {
    this.fileDrop.dragStateChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOver) => {
        this.isDragOver = isOver;
        this.cdr.markForCheck();
      });

    this.fileDrop.filesDropped
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ items, files }) => {
        if (items) {
          this.uploadQueue.handleDroppedItems(this.currentPath, items);
        } else if (files && files.length > 0) {
          this.uploadQueue.enqueue(
            this.currentPath,
            Array.from(files).map((file) => ({ file, relativePath: '' })),
          );
        }
      });

    this.fileDrop.internalDropped
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((json) => this.fileMove.drop(json, this.panelId, this.currentPath));

    this.fileMove.moved
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sourcePanelId) => this.onFilesMoved(sourcePanelId));

    this.uploadQueue.batchComplete
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onUploadBatchComplete());

    this.loadFiles();
  }

  // ── File drag-out ──

  onFileDragStart(event: DragEvent, file: FileItem): void {
    if (file.name === '..' || !file.apiPath) return;
    this.uiState.dragSource.set({ panelId: this.panelId, path: this.currentPath });

    const draggedFiles =
      this.selectedFiles.length > 0 && this.selectedFiles.some((f) => f.apiPath === file.apiPath)
        ? this.selectedFiles.filter((f) => f.apiPath && f.name !== '..')
        : [file];

    event.dataTransfer?.setData('application/json', JSON.stringify(draggedFiles));
    event.dataTransfer?.setData('text/plain', draggedFiles.map((f) => f.apiPath).join(', '));

    if (!event.dataTransfer) return;

    event.dataTransfer.effectAllowed = 'copyMove';
    const dragImage = buildDragImage(draggedFiles);
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 30, 30);
    setTimeout(() => document.body.contains(dragImage) && document.body.removeChild(dragImage), 0);
  }

  @HostListener('dragend', ['$event'])
  onFileDragEnd(event: DragEvent): void {
    this.uiState.dragSource.set(null);
  }

  get canAcceptMove(): boolean {
    return this.fileMove.canAcceptMove(this.panelId);
  }

  /** Reload the file list and notify the source panel after a successful move. */
  private onFilesMoved(sourcePanelId: string): void {
    this.loadFiles();
    this.filesMoved.emit(sourcePanelId);
    setTimeout(() => {
      this.quotaRefreshTrigger++;
      this.cdr.markForCheck();
    });
  }

  refreshAfterExternalChange(): void {
    this.loadFiles();
    this.quotaRefreshTrigger++;
  }

  applyLinkPatch(patch: LinkPatch): void {
    this.browserStore.applyLinkPatch(patch);
    this.cdr.markForCheck();
  }

  refreshIfVisible(): void {
    if (!this.paneVisible) {
      this.pendingRefresh = true;
      return;
    }
    this.loadFiles();
  }

  // ── File loading ──

  loadFiles(onLoaded?: () => void): void {
    this.stopScanPolling();
    this.browserStore.load(this.currentPath, () => {
      this.selectedFiles = [];
      this.highlightedFileName.set(null);
      this.cdr.markForCheck();
      this.startScanPollingIfNeeded();
      onLoaded?.();
    });
  }

  private highlightTimer?: ReturnType<typeof setTimeout>;

  navigateToPath(path: string, fileName?: string): void {
    this.currentPath = this.normalizePath(path);
    this.loadFiles(() => {
      if (fileName) this.flashHighlight(fileName);
    });
  }

  private flashHighlight(fileName: string): void {
    clearTimeout(this.highlightTimer);
    this.highlightedFileName.set(fileName);
    this.highlightTimer = setTimeout(() => this.highlightedFileName.set(null), 3000);
  }

  private normalizePath(path: string): string {
    const [storage = 'local', rest = ''] = path.split('://');
    const segments = rest.split('/').filter(Boolean);
    return segments.length > 0 ? `${storage}://${segments.join('/')}` : `${storage}://`;
  }

  navigateToFolder(folder: FileItem): void {
    if (folder.type !== 'folder') return;
    if (folder.name === '..') {
      this.navigateToBreadcrumb(this.browserStore.breadcrumbs().length - 2);
      return;
    }
    if (folder.apiPath) {
      this.currentPath = folder.apiPath;
      this.loadFiles();
    }
  }

  navigateToBreadcrumb(index: number): void {
    const [storage = 'local'] = this.currentPath.split('://');
    const segments = this.browserStore.breadcrumbs().slice(1, index + 1);
    this.currentPath = segments.length > 0 ? `${storage}://${segments.join('/')}` : `${storage}://`;
    this.loadFiles();
  }


  executeSearch({ filter, deep }: SearchQuery): void {
    if (!filter) {
      this.loadFiles();
      return;
    }
    this.stopScanPolling();
    this.selectedFiles = [];
    this.browserStore.search(this.currentPath, filter, deep);
  }


  triggerFileUpload(): void { this.fileInput.nativeElement.click(); }
  triggerFolderUpload(): void { this.folderInput.nativeElement.click(); }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const filesWithTarget = Array.from(input.files).map((file) => {
      const parts = file.webkitRelativePath?.split('/') ?? [];
      parts.pop();
      return { file, relativePath: parts.join('/') };
    });

    this.uploadQueue.enqueue(this.currentPath, filesWithTarget);
    input.value = '';
  }

  toggleUploadPanel(): void { this.uploadHistory.expanded.update((v) => !v); }

  closeUploadPanel(): void {
    if (this.uploadHistory.isUploading()) return;
    this.uploadQueue.clear();
    this.uploadHistory.clear();
  }

  /** Reload the file list and refresh the quota after an upload batch settles. */
  private onUploadBatchComplete(): void {
    this.loadFiles(() => {
      const scanningNames = new Set(this.files.filter((f) => f.isScanning).map((f) => f.name));
      this.uploadHistory.markScanning(scanningNames);
      this.cdr.markForCheck();
    });
    setTimeout(() => {
      this.quotaRefreshTrigger++;
      this.cdr.markForCheck();
    });
  }

  // ── Folder ──

  openNewFolderDialog(): void {
    this.showNewFolderDialog = true;
  }

  createFolderFromDialog(folderName: string): void {
    this.browserStore.setLoading(true);
    this.api.createFolder(this.currentPath, folderName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadFiles(),
        error: () => {
          this.browserStore.setLoading(false);
          this.cdr.markForCheck();
        },
      });
  }

  // ── Rename ──

  renameFromList(file: FileItem, newName: string): void {
    if (!file.apiPath) return;
    this.api.renameFile(this.currentPath, file.apiPath, newName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadFiles(),
        error: (err) => console.error('Rename failed:', err),
      });
  }

  // ── Delete ──

  deleteItem(file: FileItem): void {
    if (!file.apiPath || file.name === '..') return;
    this.deleteTarget = file;
    this.deleteMessage = `Biztosan törölni szeretné: "${file.name}"?`;
    this.showDeleteDialog = true;
  }

  startRenameSelected(): void {
    if (this.selectedFiles.length === 1) {
      this.fileListView?.startEdit(this.selectedFiles[0]);
    }
  }

  linkSelected(): void {
    const files = this.selectedFiles.filter((f) => f.type === 'file');
    if (files.length > 0) this.linkSelectedFiles.emit(files);
  }

  showFileLinks(file: FileItem): void {
    this.linksDialogFileName = file.name;
    this.linksDialogLinks = file.docmatrixLinks ?? [];
    this.showLinksDialog = true;
  }

  onNavigateToDocMatrixItem(target: DocMatrixNavigationTarget): void {
    this.showLinksDialog = false;
    this.navigateToDocMatrixItem.emit(target);
  }

  deleteSelected(): void {

    if (this.selectedFiles.length === 0) return;
    if (this.selectedFiles.some((f) => (f.links ?? 0) > 0)) {
      this.linkedItemErrorMessage = 'A kiválasztott elemek között már kapcsolva vannak, így azok nem törölhetőek!';
      this.showLinkedItemErrorDialog = true;
      return;
    }
    this.deleteTarget = null;
    this.deleteMessage = `Biztosan törölni szeretné a kiválasztott ${this.selectedFiles.length} elemet?`;
    this.showDeleteDialog = true;
  }

  confirmDelete(): void {
    this.showDeleteDialog = false;
    const onSuccess = () => { this.loadFiles(); setTimeout(() => { this.quotaRefreshTrigger++; this.cdr.markForCheck(); }); };
    const onError = () => { this.browserStore.setLoading(false); this.cdr.markForCheck(); };

    this.browserStore.setLoading(true);
    if (this.deleteTarget) {
      const { apiPath, type } = this.deleteTarget;
      this.api
        .deleteFiles(this.currentPath, [{ path: apiPath!, type: type === 'folder' ? 'dir' : 'file' }])
        .subscribe({ next: onSuccess, error: onError });
    } else {
      const items = this.selectedFiles
        .filter((f) => f.apiPath)
        .map((f) => ({ path: f.apiPath!, type: f.type === 'folder' ? ('dir' as const) : ('file' as const) }));
      this.api.deleteFiles(this.currentPath, items).subscribe({ next: onSuccess, error: onError });
    }
  }

  // ── Preview & Download ──

  previewFile(file: FileItem): void {
    if (!file.apiPath) return;
    const url = this.api.getPreviewUrl(file.apiPath);
    this.showPreview(file.name, url, file.extension);
  }

  /** Preview a file by its full storage path (e.g. triggered from the linked-files panel). */
  previewByPath(fullPath: string, fileName: string): void {
    const url = this.api.getPreviewUrl(fullPath);
    const extension = fileName.includes('.') ? fileName.split('.').pop()! : '';
    this.showPreview(fileName, url, extension);
  }

  private showPreview(name: string, url: string, extension: string): void {
    if (isImageExtension(extension)) {
      this.openInlinePreview(name, url, 'image');
    } else if ((extension || '').toLowerCase() === 'pdf') {
      this.openInlinePreview(name, url, 'pdf');
    } else {
      window.open(url, '_blank');
    }
  }

  private openInlinePreview(name: string, url: string, type: 'pdf' | 'image'): void {
    this.inlinePreviewName = name;
    this.inlinePreviewType = type;
    this.inlinePreviewRawUrl = url;
    this.inlinePreviewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.previewVisibleChange.emit(true);
    this.cdr.markForCheck();
  }

  closeInlinePreview(): void {
    this.inlinePreviewType = null;
    this.inlinePreviewSafeUrl = null;
    this.inlinePreviewRawUrl = '';
    this.inlinePreviewName = '';
    this.previewVisibleChange.emit(false);
    this.cdr.markForCheck();
  }

  downloadFile(file: FileItem): void {
    if (!file.apiPath) return;
    const url = this.api.getDownloadUrl(file.apiPath);
    this.http.get(url, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(objectUrl);
        },
        error: () => window.open(url, '_blank'),
      });
  }

}
